import { AdminButton } from '@/components/shared/AdminButton'
import { AdminFormGroup } from '@/components/shared/AdminFormGroup'
import { AdminSection } from '@/components/shared/AdminSection'
import { AdminTable } from '@/components/shared/AdminTable'
import { AdminUserLink } from '@/features/chats/AdminUserLink'
import { useUncancelLoad } from '@/hooks/useUncancelLoad'
import { friendlyDate } from '@/utils'

import type { LoadPanelProps } from './types'

export const CancelPanel = ({ load, onChange }: LoadPanelProps) => {
  const uncancelLoad = useUncancelLoad()

  const { cancel } = load
  const canUncancel =
    (load.status === 'canceled' || load.status === 'discarded') &&
    !load.isLegacy
  const showCancelDetails =
    !!cancel?.status && cancel.status !== 'not-requested'

  if (!showCancelDetails && !canUncancel) {
    return null
  }

  return (
    <AdminSection
      title="Cancel details"
      headerActions={
        !canUncancel ? null : (
          <AdminButton
            icon="redo"
            loading={uncancelLoad.isPending}
            onClick={() =>
              uncancelLoad.mutate({ loadId: load._id }, { onSuccess: onChange })
            }
            className="btn-sm"
          >
            {load.status === 'discarded' ? 'Reactivate' : 'Uncancel'}
          </AdminButton>
        )
      }
    >
      {load.status === 'discarded' && (
        <span>Load was discarded prior to booking</span>
      )}

      {cancel && (
        <>
          <div className="grid lg:grid-cols-2 xl:grid-cols-4">
            <AdminFormGroup label="status">{cancel.status}</AdminFormGroup>
            <AdminFormGroup label="date">
              {friendlyDate(cancel.date)}
            </AdminFormGroup>
            <AdminFormGroup label="method">{cancel.mode}</AdminFormGroup>
            {cancel.mode === 'email' && (
              <>
                <AdminFormGroup label="email">
                  {cancel.email?.to}
                </AdminFormGroup>
                <AdminFormGroup label="email status">
                  {cancel.email?.status}
                </AdminFormGroup>
              </>
            )}
            <AdminFormGroup label="canceled by">
              <AdminUserLink userId={cancel.canceledByUserId} />
            </AdminFormGroup>
            {cancel.canceledByAdminUserId && (
              <AdminFormGroup label="canceled by admin">
                <AdminUserLink userId={cancel.canceledByAdminUserId} />
              </AdminFormGroup>
            )}
          </div>
          {cancel?.errors?.length && (
            <>
              <div className="my-2">Errors</div>
              <AdminTable
                data={cancel.errors}
                rowKey={d => d.message}
                columns={[
                  {
                    key: 'type',
                    render: data => data.type,
                  },
                  {
                    key: 'message',
                    render: data => data.message,
                  },
                ]}
              />
            </>
          )}
        </>
      )}
    </AdminSection>
  )
}
